/**
 * School Mapper Functions
 *
 * API ↔ Domain ↔ View 간 변환 로직
 */

import type { SchoolDetailApiDto } from '../api/school.api'
import type { SchoolInGroupApiDto } from '../api/group.api'
import type { School, SchoolDetail } from '../domain/school'

// ============================================
// API → Domain
// ============================================

/**
 * API School Detail DTO → Domain School
 *
 * Note: API returns 0 for missing numeric values
 * We treat 0 as "no data" for weight/count fields
 */
export function toDomainSchool(apiSchool: SchoolDetailApiDto): School {
  // API returns 0 for missing data, treat 0 as null
  const count = apiSchool.count || null
  const weightPerFish = apiSchool.weight_per_fish || null
  const totalBiomass = count != null && weightPerFish != null
    ? count * weightPerFish
    : null

  return {
    id: apiSchool.id,
    name: apiSchool.name,
    count,
    weightPerFish, // snake_case → camelCase
    startDate: parseApiDate(apiSchool.start_date), // string → Date
    endDate: null, // API에 endDate 없음
    totalBiomass,
    averageWeight: weightPerFish,
    density: null, // Tank 정보 필요, 별도 계산
    feedingRate: null, // API에 없음
  }
}

/**
 * API School (Group 내) → Domain School
 *
 * Note: API returns 0 for missing numeric values (weight_per_fish, count)
 * We treat 0 as "no data" for weight fields (fish cannot weigh 0g)
 * Count of 0 is also treated as "no data" in school context
 */
export function toDomainSchoolFromGroup(apiSchool: SchoolInGroupApiDto): School {
  // API returns 0 for missing data, treat 0 as null for these fields
  const count = apiSchool.count || null
  const weightPerFish = apiSchool.weight_per_fish || null
  const totalBiomass = count != null && weightPerFish != null
    ? count * weightPerFish
    : null

  return {
    id: apiSchool.id,
    name: apiSchool.name,
    count,
    weightPerFish,
    startDate: parseApiDate(apiSchool.start_date),
    endDate: null,
    totalBiomass,
    averageWeight: weightPerFish,
    density: null, // Tank 정보 필요
    feedingRate: null, // API에 없음
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * API Date String → Date 객체
 */
function parseApiDate(dateString: string): Date {
  return new Date(dateString)
}

/**
 * Date 객체 → YYYYMMDD 형식 문자열
 */
export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}
